import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Table, Badge, Spinner, Button, Form, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCheckCircle,
    faExternalLinkAlt,
    faSearch,
    faSync,
    faDownload,
    faRedo
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';

import { db } from 'config/firebase.js';
import { collection, query, orderBy, onSnapshot, where, limit } from 'firebase/firestore';
import missingImage from 'assets/merlin/missing_image.png';

const CLOUD_FUNCTIONS_URL =
    process.env.REACT_APP_CLOUD_FUNCTIONS_URL || 'https://us-central1-stk-merlin.cloudfunctions.net';

/**
 * VerifiedTab - View all verified product-ASIN mappings.
 * 
 * Features:
 * - Display verified and auto-matched products
 * - Export functionality
 * - Re-verify option
 */
const VerifiedTab = () => {
    const [verifications, setVerifications] = useState([]);
    const [products, setProducts] = useState({});
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [filter, setFilter] = useState('all'); // 'all', 'verified', 'auto_matched'

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Real-time listener for verified products
    useEffect(() => {
        setLoading(true);

        const statuses = filter === 'all'
            ? ['verified', 'auto_matched']
            : [filter];

        const verificationsQuery = query(
            collection(db, 'productCatalog/topicz/asinVerifications'),
            where('verificationStatus', 'in', statuses),
            orderBy('discoveredAt', 'desc'),
            limit(200)
        );

        const unsubscribe = onSnapshot(
            verificationsQuery,
            (snapshot) => {
                const data = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setVerifications(data);
                setLoading(false);
            },
            (error) => {
                console.error('Error fetching verified products:', error);
                toast.error('Error loading verified products');
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, [filter]);

    // Fetch product data
    useEffect(() => {
        const fetchProducts = async () => {
            const skusToFetch = verifications
                .map(v => v.sku)
                .filter(sku => sku && !products[sku]);

            if (skusToFetch.length === 0) return;

            const productQuery = query(
                collection(db, 'productCatalog/topicz/products'),
                where('sku', 'in', skusToFetch.slice(0, 30))
            );

            try {
                const unsubscribe = onSnapshot(productQuery, (snapshot) => {
                    const newProducts = { ...products };
                    snapshot.docs.forEach(doc => {
                        const data = doc.data();
                        if (data.sku) {
                            newProducts[data.sku] = data;
                        }
                    });
                    setProducts(newProducts);
                });
                return () => unsubscribe();
            } catch (error) {
                console.error('Error fetching products:', error);
            }
        };

        if (verifications.length > 0) {
            fetchProducts();
        }
    }, [verifications]);

    // Filter by search
    const filteredVerifications = debouncedSearchTerm
        ? verifications.filter(v => {
            const product = products[v.sku];
            const searchLower = debouncedSearchTerm.toLowerCase();
            return (
                v.sku?.toLowerCase().includes(searchLower) ||
                product?.title?.toLowerCase().includes(searchLower) ||
                product?.vendor?.toLowerCase().includes(searchLower) ||
                v.verifiedAsin?.toLowerCase().includes(searchLower)
            );
        })
        : verifications;

    // Format date
    const formatDate = (timestamp) => {
        if (!timestamp) return '-';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString();
    };

    // Export to CSV
    const handleExport = () => {
        const headers = ['SKU', 'ASIN', 'Title', 'Vendor', 'Confidence', 'Status', 'Verified Date'];
        const rows = filteredVerifications.map(v => {
            const product = products[v.sku];
            const confidence = v.approvedCandidate?.confidence?.overall || 0;
            return [
                v.sku,
                v.verifiedAsin || '',
                (product?.title || '').replace(/,/g, ' '),
                product?.vendor || '',
                Math.round(confidence * 100) + '%',
                v.verificationStatus,
                formatDate(v.verifiedAt)
            ];
        });

        const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `verified-asins-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);

        toast.success(`Exported ${filteredVerifications.length} products`, { theme: 'colored' });
    };

    // Re-verify (force rediscovery)
    const handleReverify = async (sku) => {
        try {
            const response = await fetch(`${CLOUD_FUNCTIONS_URL}/asinDiscover`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    skus: [sku],
                    options: { force: true }
                })
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Re-verification failed');
            }

            toast.success(`Re-verification started for ${sku}`, { theme: 'colored' });
        } catch (error) {
            console.error('Error re-verifying:', error);
            toast.error(error.message);
        }
    };

    // Stats
    const verifiedCount = verifications.filter(v => v.verificationStatus === 'verified').length;
    const autoMatchedCount = verifications.filter(v => v.verificationStatus === 'auto_matched').length;

    return (
        <div className="p-3">
            {/* Stats Row */}
            <Row className="mb-3">
                <Col md={4}>
                    <Card className="text-center h-100">
                        <Card.Body>
                            <div className="fs-2 fw-bold text-success">{verifiedCount}</div>
                            <div className="text-muted small">Manually Verified</div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="text-center h-100">
                        <Card.Body>
                            <div className="fs-2 fw-bold text-info">{autoMatchedCount}</div>
                            <div className="text-muted small">Auto-Matched</div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="text-center h-100">
                        <Card.Body>
                            <div className="fs-2 fw-bold text-primary">{verifications.length}</div>
                            <div className="text-muted small">Total Verified</div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Action Bar */}
            <Card className="mb-3">
                <Card.Body className="py-2">
                    <Row className="align-items-center">
                        <Col md={3}>
                            <Form.Select
                                size="sm"
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                            >
                                <option value="all">All Verified</option>
                                <option value="verified">Manually Verified</option>
                                <option value="auto_matched">Auto-Matched</option>
                            </Form.Select>
                        </Col>
                        <Col md={5}>
                            <div className="input-group input-group-sm">
                                <span className="input-group-text">
                                    <FontAwesomeIcon icon={faSearch} />
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search by SKU, title, vendor, or ASIN..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </Col>
                        <Col md={4} className="text-end">
                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={handleExport}
                                disabled={filteredVerifications.length === 0}
                            >
                                <FontAwesomeIcon icon={faDownload} className="me-1" />
                                Export CSV
                            </Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Main Table */}
            {loading ? (
                <div className="text-center py-5">
                    <Spinner animation="border" />
                    <p className="mt-2 text-muted">Loading verified products...</p>
                </div>
            ) : filteredVerifications.length === 0 ? (
                <Alert variant="info" className="text-center">
                    No verified products found. Start discovering ASINs in the Discover tab!
                </Alert>
            ) : (
                <div style={{ maxHeight: 500, overflowY: 'auto' }}>
                    <Table hover responsive size="sm" className="align-middle">
                        <thead className="sticky-top bg-white">
                            <tr>
                                <th style={{ width: 50 }}>Image</th>
                                <th>Product</th>
                                <th style={{ width: 130 }}>SKU</th>
                                <th style={{ width: 120 }}>ASIN</th>
                                <th style={{ width: 90 }}>Confidence</th>
                                <th style={{ width: 90 }}>Status</th>
                                <th style={{ width: 100 }}>Verified</th>
                                <th style={{ width: 50 }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredVerifications.map(verification => {
                                const product = products[verification.sku];
                                const confidence = verification.approvedCandidate?.confidence?.overall || 0;
                                const isAutoMatched = verification.verificationStatus === 'auto_matched';

                                return (
                                    <tr key={verification.sku}>
                                        <td>
                                            <img
                                                src={product?.images?.[0] || missingImage}
                                                alt=""
                                                style={{ width: 40, height: 40, objectFit: 'contain' }}
                                                onError={(e) => { e.target.src = missingImage; }}
                                            />
                                        </td>
                                        <td>
                                            <div className="fw-normal text-truncate small" style={{ maxWidth: 250 }}>
                                                {product?.title || verification.sku}
                                            </div>
                                            <small className="text-muted">{product?.vendor || '-'}</small>
                                        </td>
                                        <td>
                                            <code className="fs--2">{verification.sku}</code>
                                        </td>
                                        <td>
                                            {verification.verifiedAsin ? (
                                                <a
                                                    href={`https://www.amazon.com/dp/${verification.verifiedAsin}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-decoration-none"
                                                >
                                                    <code className="fs--2">{verification.verifiedAsin}</code>
                                                    <FontAwesomeIcon icon={faExternalLinkAlt} className="ms-1 fs--2 text-muted" />
                                                </a>
                                            ) : (
                                                <span className="text-muted">-</span>
                                            )}
                                        </td>
                                        <td>
                                            <Badge bg={confidence >= 0.9 ? 'success' : confidence >= 0.7 ? 'warning' : 'secondary'}>
                                                {Math.round(confidence * 100)}%
                                            </Badge>
                                        </td>
                                        <td>
                                            <Badge bg={isAutoMatched ? 'info' : 'success'}>
                                                <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                                                {isAutoMatched ? 'Auto' : 'Verified'}
                                            </Badge>
                                        </td>
                                        <td>
                                            <small className="text-muted">{formatDate(verification.verifiedAt)}</small>
                                        </td>
                                        <td>
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                title="Re-verify"
                                                onClick={() => handleReverify(verification.sku)}
                                            >
                                                <FontAwesomeIcon icon={faRedo} />
                                            </Button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </div>
            )}

            <div className="text-center text-muted small mt-2">
                Showing {filteredVerifications.length} verified products (limited to 200)
            </div>
        </div>
    );
};

export default VerifiedTab;
